import React from 'react';
import {
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  CreateOutlined as CreateIcon,
  FolderOutlined as FolderIcon,
  DescriptionOutlined as DescriptionIcon,
  ArrowForwardOutlined as ArrowForwardIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const MAX_RESULTS_PER_TYPE = 3; // Show max 3 results per type in dropdown

const SearchAutocomplete = ({ searchTerm, results, loading, onClose, showViewAll = true, embedded = false }) => {
  const navigate = useNavigate();
  const paperSx = {
    position: embedded ? 'relative' : 'absolute',
    top: embedded ? 'auto' : '100%',
    left: 0,
    right: 0,
    mt: 1,
    zIndex: 1300,
    maxHeight: embedded ? 'min(60vh, 520px)' : 500,
    overflow: 'auto',
    boxShadow: embedded ? 1 : 3
  };

  if (!searchTerm || !searchTerm.trim()) {
    return null;
  }

  if (loading) {
    return (
      <Paper
        sx={{ ...paperSx, p: 2 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (!results || results.total === 0) {
    return (
      <Paper
        sx={{ ...paperSx, p: 2 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
          No results found
        </Typography>
      </Paper>
    );
  }

  const prompts = (results.prompts || []).slice(0, MAX_RESULTS_PER_TYPE);
  const collections = (results.collections || []).slice(0, MAX_RESULTS_PER_TYPE);
  const contexts = (results.contexts || []).slice(0, MAX_RESULTS_PER_TYPE);
  const totalShown = prompts.length + collections.length + contexts.length;
  const totalResults = results.total || 0;
  const hasMore = totalResults > totalShown;

  const handlePromptClick = (prompt) => {
    navigate(`/library/${prompt.id}`);
    onClose();
  };

  const handleCollectionClick = (collection) => {
    navigate(`/collections/${collection.id}`);
    onClose();
  };

  const handleContextClick = (context) => {
    navigate(`/contexts/${context.id}`);
    onClose();
  };

  const handleViewAll = () => {
    navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
    onClose();
  };

  return (
    <Paper
      sx={paperSx}
    >
      {/* Capabilities section */}
      {prompts.length > 0 && (
        <>
          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CreateIcon fontSize="small" color="primary" />
              <Typography variant="caption" fontWeight={600} color="text.primary">
                CAPABILITIES
              </Typography>
            </Box>
          </Box>
          <List dense sx={{ py: 0 }}>
            {prompts.map((prompt, index) => (
              <React.Fragment key={prompt.id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handlePromptClick(prompt)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CreateIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 500 }}>
                          {prompt.title || 'Untitled capability'}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {prompt.description || 'No description'}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < prompts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </>
      )}

      {/* Projects section */}
      {collections.length > 0 && (
        <>
          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderIcon fontSize="small" color="primary" />
              <Typography variant="caption" fontWeight={600} color="text.primary">
                PROJECTS
              </Typography>
            </Box>
          </Box>
          <List dense sx={{ py: 0 }}>
            {collections.map((collection, index) => (
              <React.Fragment key={collection.id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleCollectionClick(collection)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <FolderIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 500 }}>
                          {collection.title || 'Untitled Project'}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {collection.description || 'No description'}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < collections.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </>
      )}

      {/* Knowledge section */}
      {contexts.length > 0 && (
        <>
          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon fontSize="small" color="primary" />
              <Typography variant="caption" fontWeight={600} color="text.primary">
                KNOWLEDGE
              </Typography>
            </Box>
          </Box>
          <List dense sx={{ py: 0 }}>
            {contexts.map((context, index) => (
              <React.Fragment key={context.id}>
                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleContextClick(context)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <DescriptionIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" noWrap sx={{ color: 'text.primary', fontWeight: 500 }}>
                          {context.name || 'Untitled knowledge'}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {context.description || 'No description'}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < contexts.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </>
      )}

      {/* View All Results Link */}
      {showViewAll && hasMore && (
        <>
          <Divider />
          <ListItem disablePadding>
            <ListItemButton onClick={handleViewAll}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ArrowForwardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" color="primary">
                    View all {totalResults} results
                  </Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        </>
      )}
    </Paper>
  );
};

export default SearchAutocomplete;

